import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
// 👇 IMPORTAMOS la interfaz, NO la definimos aquí para evitar error de HMR
import { notificationsService, type ApiNotification } from '../services/notificationsService';
import toast from 'react-hot-toast';

interface SupplierNotificationsContextType {
    notifications: ApiNotification[];
    unreadCount: number;
    loading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
    deleteNotifications: (ids: string[]) => Promise<void>;
}

const SupplierNotificationsContext = createContext<SupplierNotificationsContextType | undefined>(undefined);

export const SupplierNotificationsProvider = ({ children }: { children: ReactNode }) => {
    const [notifications, setNotifications] = useState<ApiNotification[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = async () => {
        const token = localStorage.getItem('supplier_access_token');
        if (!token) { setLoading(false); return; }

        try {
            const data = await notificationsService.getAll(true);
            const sorted = Array.isArray(data)
                ? data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                : [];
            setNotifications(sorted);
        } catch {
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const markAsRead = async (id: string) => {
        // 👇 Sincronización instantánea
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
        try { await notificationsService.markAsRead(id, true); } catch { fetchNotifications(); }
    };

    const markAllAsRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        try { await notificationsService.markAllAsRead(true); toast.success('Todas leídas'); } catch { fetchNotifications(); }
    };

    const deleteNotification = async (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        try {
            await notificationsService.delete([id], true);
            toast.success('Eliminada');
        } catch {
            fetchNotifications();
        }
    };

    const deleteNotifications = async (ids: string[]) => {
        setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
        try {
            await notificationsService.delete(ids, true);
            toast.success('Eliminadas');
        } catch {
            fetchNotifications();
        }
    };

    return (
        <SupplierNotificationsContext.Provider value={{
            notifications,
            unreadCount: notifications.filter(n => !n.isRead).length,
            loading,
            fetchNotifications,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            deleteNotifications
        }}>
            {children}
        </SupplierNotificationsContext.Provider>
    );
};

export const useSupplierNotifications = () => {
    const context = useContext(SupplierNotificationsContext);
    if (!context) throw new Error('useSupplierNotifications debe usarse dentro de SupplierNotificationsProvider');
    return context;
};